/* screens/sales/planEditorParts.jsx — общие куски редактора тарифа по вкладкам:
   вкладки с отметкой ошибки, выбор B2C/B2B, срок «1/2/3/6/12 мес. или свой», скидка к
   помесячной цене, подсказка заморозки. Один набор на «Закрытые» и «Публичные»
   (владелец 18.10: «те же вкладки — в редакторе закрытых тарифов»): разойдись
   подписи или правило срока в двух копиях — одно и то же поле в соседних вкладках
   раздела вело бы себя по-разному. Логика — planEditor.js, здесь только вёрстка. */
import React from 'react';
import { Tabs } from '../../components/patterns/Tabs.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field, NumberInput, Segmented } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Switch } from '../../components/ui/Switch.jsx';
import { PERIOD_FIELD } from './planForm.jsx';
import {
  PLAN_TABS, PERIOD_OPTIONS, TERM_OPTIONS, CUSTOM, errorTabs, firstErrorTab, periodChoiceValue, periodHint,
  termChoiceValue, termMonths, monthsToDays, discountError, discountHint,
} from './planEditor.js';

const { useState, useRef, useEffect } = React;

/* ── B2C / B2B ──
   Явный выбор вместо списка «Аудитория»: слово «аудитория» менеджер читал как
   «кому продаём», а поле решает, в какое пространство тариф вообще ставится. */
export const AUDIENCE_OPTIONS = [
  { value: 'personal', label: 'B2C — личный ассистент' },
  { value: 'business', label: 'B2B — заведение' },
];
export const AUDIENCE_HINT = 'B2C — личное пространство, B2B — пространство заведения. Определяет вкладку витрины у клиента: бизнес-тариф на личное пространство не выставить.';

export function AudienceField({ value, onChange, disabled, error, hint }) {
  return (
    <Field label="B2C или B2B" error={error} hint={hint || AUDIENCE_HINT}>
      <Segmented className="sc-plan-aud" options={AUDIENCE_OPTIONS} value={value} onChange={onChange}
        disabled={!!disabled} ariaLabel="Аудитория тарифа" />
    </Field>
  );
}

/* ── Срок: 1/2/3/6/12 мес. или свой в днях ── */
export function PeriodField({ form, onChoice, onDays, disabled, error }) {
  const choice = periodChoiceValue(form);
  const custom = choice === CUSTOM;
  return (
    <Field label="Срок" error={error} hint={periodHint(form.period_days, custom)}>
      <div className="sc-plan-period stack stack-6">
        <Segmented className="sc-plan-seg" options={PERIOD_OPTIONS} value={choice} onChange={onChoice}
          disabled={!!disabled} ariaLabel="Срок тарифа" />
        {custom && (
          // Без max: лишнее не прижимаем молча к 3660 на потере фокуса — скажет проверка формы.
          <NumberInput value={form.period_days} unit={PERIOD_FIELD.unit} min={PERIOD_FIELD.min}
            invalid={!!error} disabled={!!disabled} onChange={onDays} aria-label="Срок тарифа, дней" />
        )}
      </div>
    </Field>
  );
}

/* ── Срок «Нового срока»: 2/3/6/12 мес. или свой (2–36 мес.) ──
   Месяцами, а не днями: дни сервер собирает сам (30 × N) — так же, как у живой линейки. */
export function TermMonthsField({ form, onChoice, onMonths, error }) {
  const choice = termChoiceValue(form);
  const m = termMonths(form);
  const hint = m && m >= 2 && m <= 36
    ? `${m} мес. — ${monthsToDays(m)} дней (месяц — 30 дней).`
    : 'От 2 до 36 месяцев; месяц — 30 дней.';
  return (
    <Field label="Срок" error={error} required hint={hint}>
      <div className="sc-plan-period stack stack-6">
        <Segmented className="sc-plan-seg" options={TERM_OPTIONS} value={choice} onChange={onChoice}
          ariaLabel="Срок нового варианта" />
        {choice === CUSTOM && (
          <NumberInput value={form.months} unit="мес." min={2} max={36} invalid={!!error} onChange={onMonths}
            aria-label="Срок, месяцев" />
        )}
      </div>
    </Field>
  );
}

/* ── Скидка к помесячной цене ──
   Без min/max у поля: NumberInput на потере фокуса прижимает значение к границам, и
   выведенная из цены «−2 %» молча стала бы «0 %» — и пересчитала бы цену. Границы
   проверяет discountError, только у набранного руками; та же ошибка закрывает
   сохранение (validatePublicForm), поэтому здесь она живая, без ожидания «Сохранить».
   Вернуть сюда min={0} — снова переписать цену фокусом (раунд 1): это держат два теста
   planEditorTabs.test.jsx — пропсы поля и потеря фокуса настоящим NumberInput на «−2 %». */
export function DiscountField({ form, base, months, onChange }) {
  const error = discountError(form);
  return (
    <Field label="Скидка к помесячной цене, %" error={error} hint={discountHint(form, base, months)}>
      <NumberInput value={form.discount_pct} unit="%" invalid={!!error} onChange={onChange}
        aria-label="Скидка к помесячной цене, %" />
    </Field>
  );
}

/* ── Переключатель с подсказкой ── */
export function Toggle({ title, hint, checked, onChange, disabled, error }) {
  return (
    <div className="sc-plans-toggle">
      <span className="sc-plans-toggle__text">
        <span className="sc-plans-toggle__title">{title}</span>
        {hint && <span className="sc-plans-toggle__hint">{hint}</span>}
        {error && <span className="sc-pub-field-error" role="alert">{error}</span>}
      </span>
      <Switch checked={!!checked} onChange={onChange} disabled={disabled} ariaLabel={title} />
    </div>
  );
}

/* ── Почему поле заморожено — рядом с самим полем ──
   Замороженные поля живут на трёх вкладках (B2C/B2B — «Основное», срок и
   автопродление — «Цена и срок», «после текущего» — «Продажи»): одна подсказка над
   формой осталась бы на чужой вкладке, а выключенное поле — без объяснения. */
export function FrozenNote({ hint, onCopyInstead }) {
  if (!hint) return null;
  return (
    <div className="sc-plans-warn sc-pub-frozen" role="status">
      <Icon name="lock" size={16} />
      <span className="grow">{hint}</span>
      {onCopyInstead && (
        <Button size="sm" variant="secondary" iconLeft="layers" onClick={onCopyInstead}>Создать копию</Button>
      )}
    </div>
  );
}

/* ── Вкладки ── */
export function TabLabel({ label, error }) {
  return (
    <>
      {label}
      {error && (
        <span className="sc-plan-tab__mark" title="На вкладке есть ошибки">
          <Icon name="alert-circle" size={14} />
          <span className="visually-hidden">— есть ошибки</span>
        </span>
      )}
    </>
  );
}

export function EditorTabs({ value, onChange, errors, tabs = PLAN_TABS }) {
  const marked = errorTabs(errors);
  const items = tabs.map((t) => ({ value: t.value, label: <TabLabel label={t.label} error={marked.has(t.value)} /> }));
  return <Tabs className="sc-plan-tabs" items={items} value={value} onChange={onChange} ariaLabel="Настройки тарифа" />;
}

/* Панель вкладки. Неактивные — `hidden`, а не размонтированы: набранный слаг новой
   фичи и прочее локальное состояние полей не пропадает при переходе между вкладками. */
export function TabPanel({ value, active, label, children }) {
  return (
    <div role="tabpanel" aria-label={label} data-tab={value} hidden={!active} className="sc-plan-panel stack stack-12">
      {children}
    </div>
  );
}

/* Переход к первой ошибке открытой вкладки: вкладку — в кадр (на узком экране ряд
   вкладок прокручивается), поле — в центр, фокус — на его контрол. Без фокуса
   клавиатура и экранный диктор оставались на кнопке «Сохранить», и человек слышал
   «Проверьте отмеченные поля», не узнав, какое (a11y, раунд 2 верификатора).
   Фокус — без второй прокрутки (preventScroll): поле уже стоит по центру. У кнопок
   срока фокус — на выбранной (roving tabindex Segmented), у ошибки переключателя — на
   сам переключатель в его строке. Выключенный контрол (замороженное поле) фокуса не
   получает — тогда только прокрутка. Вернёт сфокусированный контрол или null. */
const OPEN_PANEL = '[role="tabpanel"]:not([hidden])';
const FOCUSABLE = 'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function revealFirstError(root) {
  if (!root || !root.querySelector) return null;
  const active = root.querySelector('[role="tab"][aria-selected="true"]');
  if (active && active.scrollIntoView) active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  const bad = root.querySelector(`${OPEN_PANEL} .field--error, ${OPEN_PANEL} .sc-pub-field-error`);
  if (!bad) return null;
  if (bad.scrollIntoView) bad.scrollIntoView({ block: 'center' });
  const box = (bad.closest && bad.closest('.field--error, .sc-plans-toggle')) || bad;
  const control = box.querySelector
    ? box.querySelector('[role="radio"][aria-checked="true"]:not([disabled])') || box.querySelector(FOCUSABLE)
    : null;
  if (!control || !control.focus) return null;
  control.focus({ preventScroll: true });
  return control;
}

/* Состояние вкладок редактора и переход к ошибке: при сохранении с ошибками —
   первая (по порядку вкладок) вкладка с ошибкой, поле с ошибкой — в кадр и в фокус.
   Ошибка на невидимой вкладке иначе выглядела бы как «Проверьте отмеченные поля» без
   единой отметки. Переход — после перерисовки (эффект), когда панель уже открыта. */
export function useEditorTabs(initial = PLAN_TABS[0].value) {
  const [tab, setTab] = useState(initial);
  const [jump, setJump] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (jump) revealFirstError(ref.current);
  }, [jump]);
  const showErrors = (errors) => {
    const t = firstErrorTab(errors);
    if (!t) return;
    setTab(t);
    setJump((n) => n + 1);
  };
  return { tab, setTab, ref, showErrors };
}
